import { type DocumentPickerAsset, getDocumentAsync } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useEffect, useRef, useState } from "react";
import { FileUp as FileUpIcon, Image as ImageIcon } from "@tamagui/lucide-icons";
import { type IconComponent, isWeb, Modal, ResourceCard, startPickerNoResponseTimer, type UseFilePickerOptions, useFilePicker, useImagePicker, YStack } from "@medbillai/ui";
import type { DocumentScanEntryPoint } from "@medbillai/utils/documentScan";
import type { UploadMethod } from "@medbillai/utils/documentUpload";
import { generateFileName, isSupportedImageType } from "@medbillai/utils/images";
import { analyticsService } from "../../lib/analytics";
import type { UploadErrorType, UploadPickerOutcome } from "../../lib/analytics/types";
import { createUploadFunnelSession, sumDefinedSizes, type UploadFunnelAttempt, type UploadFunnelSession } from "../../lib/analytics/uploadFunnel";
import type { UploadError, UploadFile } from "../../provider/documents";
import { CameraPickerButton } from "./components/CameraPickerButton";
import { assetToBlob } from "./utils/assetToBlob";
import { openScanbot } from "./utils/open-scanbot/openScanbot";

// Distinguishes a user-format rejection from a real conversion failure in the
// shared catch — a GIF converts fine and only then fails the type check, and
// reporting that as BLOB_CONVERSION_FAILED would dress it as infrastructure.
class UnsupportedFileTypeError extends Error {}
export type UploadDocumentSheetProps = {
  open: boolean;
  onUploadError: (error: UploadError) => void;
  onOpenChange: (open: boolean) => void;
  onCameraPermissionCheckStart: () => void;
  onCameraPermissionCheckComplete: () => void;
  onCameraPermissionDenied?: () => void;
  /**
   * `funnelAttempt` is the frozen funnel envelope for the pick that produced
   * these files. Thread it into `upload()` so the outcome events and persisted
   * provenance join the attempt that actually picked the files.
   */
  onFilesLoaded: (files: UploadFile[], funnelAttempt?: UploadFunnelAttempt) => Promise<void>;
  cleanupBeforeUpload?: () => void;
  title?: string;
  subtitle?: string;
  /** Case id when scanning from a case-scoped surface. */
  issueId?: string;
  /** Product surface that opened the upload sheet; defaults to OTHER. */
  entryPoint?: DocumentScanEntryPoint;
  /**
   * File-picker constraints for "Upload a file". Defaults to the picker's
   * own accept list (PDF + JPEG/PNG) and multiplicity.
   */
  filePickerOptions?: Pick<UseFilePickerOptions, "accept" | "multiple">;
  /**
   * Bumps whenever the host opens a new pick request. Web has no sheet, so
   * retries while `open` stays true (or after a stuck Expo promise) need a
   * signal beyond the rising edge of `open`.
   */
  pickerGeneration?: number;
  /**
   * A third choice alongside the two upload ones, for a caller whose question
   * has an answer other than a document — "I'm not insured" on the member-card
   * sheet, where an uninsured user has nothing to upload.
   */
  alternativeAnswer?: {
    icon: IconComponent;
    label: string;
    onPress: () => void;
    testID?: string;
  };
};

/** Disambiguate duplicate basenames from multi-folder picks (`scan.png` ×2). */
const uniqueUploadName = (name: string, usedNames: Map<string, number>) => {
  const count = usedNames.get(name) ?? 0;
  usedNames.set(name, count + 1);
  if (count === 0) {
    return name;
  }
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) {
    return `${name}-${count + 1}`;
  }
  return `${name.slice(0, lastDot)}-${count + 1}${name.slice(lastDot)}`;
};
const DEFAULT_FILE_ACCEPT = ["application/pdf", "image/jpeg", "image/png"] as const;
const documentAssetsToUploadFiles = async (files: DocumentPickerAsset[]): Promise<UploadFile[]> => {
  const usedNames = new Map<string, number>();
  const uploadFiles: UploadFile[] = [];
  for (const file of files) {
    const blob = await assetToBlob(file);
    // Prefer the browser File.type; fall back to the picker's mimeType
    // when the OS left type empty (common for PDFs).
    const contentType = blob.type || file.mimeType || "";
    uploadFiles.push({
      name: uniqueUploadName(file.name, usedNames),
      sizeBytes: file.size ?? blob.size,
      contentType,
      blob
    });
  }
  return uploadFiles;
};
type AttemptRecord = {
  attempt: UploadFunnelAttempt;
  openedAt: number;
  terminalEmitted: boolean;
  /** Set when the picker reports a selection; the size the batch failed at. */
  totalSizeBytes?: number;
};

// `hadUserActivation: false` means the browser refused the shim's synthetic
// click, so the dialog never opened — the measured hang. Anything else is
// ambiguous between a hang and a cancel the shim never reported.
const noResponseExtra = (info: {
  hadUserActivation: boolean | undefined;
}): {
  errorType?: UploadErrorType;
} => info.hadUserActivation === false ? {
  errorType: "ACTIVATION_LOST"
} : {};
export const UploadDocumentSheet = ({
  open,
  onUploadError,
  onOpenChange,
  onCameraPermissionCheckStart,
  onCameraPermissionCheckComplete,
  onCameraPermissionDenied,
  onFilesLoaded,
  cleanupBeforeUpload,
  title = "Upload",
  subtitle,
  issueId,
  entryPoint = "OTHER",
  filePickerOptions,
  pickerGeneration = 0,
  alternativeAnswer
}: UploadDocumentSheetProps) => {
  const [useCamera, setUseCamera] = useState<boolean>(false);
  // A caller offering an alternative answer needs the chooser rendered on
  // every platform: web's usual skip-to-OS-dialog path would open the file
  // picker before the alternative is ever visible, leaving a user who has no
  // document to upload (e.g. uninsured on the member-card sheet) stuck.
  const hasAlternativeAnswer = alternativeAnswer !== undefined;
  // Web skips the chooser sheet and opens the OS file picker on open /
  // generation bumps. Track the last handled generation so Strict Mode
  // remounts don't re-open the dialog for the same request.
  const lastHandledGenerationRef = useRef<number | null>(null);
  // Live generation so a superseded pick can ignore its own resolution
  // without clearing the newer request (overlapping OS dialogs).
  const currentPickerGenerationRef = useRef(pickerGeneration);
  const sessionRef = useRef<UploadFunnelSession | null>(null);
  // Per-method attempt records, frozen at that method's tap. Keyed by method —
  // not one shared ref — so Gallery still converting while File is tapped keeps
  // its own envelope and start time. Async handlers read the record once, at
  // entry: the sheet stays open across blob conversion (ENG-6644), so a
  // close → reopen → same-method tap during that window replaces the entry, and
  // a lookup after the await would file the first pick's files and failures
  // under the second pick's session.
  const methodAttemptsRef = useRef<Partial<Record<UploadMethod, AttemptRecord>>>({});
  const methodSelectedRef = useRef(false);
  // The session is minted once per sheet open; `entryPoint`/`issueId` are in
  // the deps only for lint honesty — a prop that settles while the sheet is
  // already open must not re-mint mid-flow (the envelope is frozen at open,
  // like the per-method attempts frozen at their tap).
  const sessionMintedForOpenRef = useRef(false);

  // Native only: the chooser sheet render is the funnel's top. Web mints its
  // session per picker generation instead — the trigger tap and the OS dialog
  // are the same moment there, so it lives in the web pick effect below.
  useEffect(() => {
    if (!open || isWeb && !hasAlternativeAnswer) {
      sessionMintedForOpenRef.current = false;
      return;
    }
    if (sessionMintedForOpenRef.current) {
      return;
    }
    sessionMintedForOpenRef.current = true;
    methodSelectedRef.current = false;
    const session = createUploadFunnelSession({
      entryPoint,
      issueId
    });
    sessionRef.current = session;
    analyticsService.trackEvent("UploadInitiated", session);
  }, [open, entryPoint, issueId, hasAlternativeAnswer]);
  const selectMethod = useCallback((method: UploadMethod) => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }
    methodSelectedRef.current = true;
    methodAttemptsRef.current[method] = {
      attempt: {
        ...session,
        method
      },
      openedAt: Date.now(),
      terminalEmitted: false
    };
    analyticsService.trackEvent("UploadMethodSelected", {
      ...session,
      method
    });
  }, []);
  const trackPickerReturned = useCallback((record: AttemptRecord | undefined, outcome: UploadPickerOutcome, fileCount: number, extra?: {
    totalSizeBytes?: number;
    errorType?: UploadErrorType;
  }) => {
    // One terminal per picker open: a consumer callback that throws after
    // SELECTED routes through the picker hook's onError, which must not
    // stack an ERROR terminal onto an attempt that already reported.
    if (!record || record.terminalEmitted) {
      return;
    }
    record.terminalEmitted = true;
    // Carried on the record so a failure after the pick reports the size it
    // failed at — the conversion failures below are the memory-pressure
    // suspects, and that hypothesis is only testable against batch size.
    if (extra?.totalSizeBytes !== undefined) {
      record.totalSizeBytes = extra.totalSizeBytes;
    }
    analyticsService.trackEvent("UploadPickerReturned", {
      ...record.attempt,
      outcome,
      fileCount,
      ...(extra?.totalSizeBytes !== undefined ? {
        totalSizeBytes: extra.totalSizeBytes
      } : {}),
      ...(extra?.errorType ? {
        errorType: extra.errorType
      } : {}),
      durationMs: Date.now() - record.openedAt
    });
  }, []);

  /**
   * Failures between a successful pick and the upload provider — blob
   * conversion, unsupported type. Without this the attempt would end at
   * `UploadPickerReturned: SELECTED` and simply stop, which reads downstream as
   * an unexplained drop rather than a known failure.
   */
  const trackUploadFailed = useCallback((record: AttemptRecord | undefined, fileCount: number, errorType: UploadErrorType): void => {
    if (!record) {
      return;
    }
    analyticsService.trackEvent("UploadFailed", {
      ...record.attempt,
      fileCount,
      ...(record.totalSizeBytes !== undefined ? {
        totalSizeBytes: record.totalSizeBytes
      } : {}),
      errorType,
      durationMs: Date.now() - record.openedAt
    });
  }, []);
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && sessionRef.current && !methodSelectedRef.current) {
      analyticsService.trackEvent("UploadSheetDismissed", sessionRef.current);
      // A parent-driven close can echo back through here; one dismissal per
      // sheet open.
      sessionRef.current = null;
    }
    onOpenChange(nextOpen);
  }, [onOpenChange]);
  const openCamera = async () => {
    const record = methodAttemptsRef.current.SCANNER;
    try {
      setUseCamera(true);
      let scannedImages: Awaited<ReturnType<typeof openScanbot>>;
      try {
        scannedImages = await openScanbot({
          entryPoint,
          issueId,
          uploadSessionId: record?.attempt.uploadSessionId
        });
      } catch (error) {
        // Emitted here, not in the outer catch, so a failure in the upload work
        // below can't produce a second terminal for the same scanner open.
        trackPickerReturned(record, "ERROR", 0, {
          errorType: "PICKER_ERROR"
        });
        throw error;
      }
      if (scannedImages.status === "CANCELED") {
        trackPickerReturned(record, "CANCELED", 0);
        return;
      }
      if (scannedImages.images.length > 0) {
        trackPickerReturned(record, "SELECTED", scannedImages.images.length);
        await multiPhotoUpload(scannedImages.images, record);
      } else {
        trackPickerReturned(record, "ERROR", 0, {
          errorType: "PICKER_ERROR"
        });
        throw new Error("No valid images found");
      }
    } catch (error) {
      console.error("Error during document scanning:", error);
      onUploadError({
        error: error instanceof Error ? error : new Error(String(error))
      });
    } finally {
      setUseCamera(false);
    }
  };
  const onError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      onUploadError({
        error
      });
      return;
    }
    onUploadError({
      error: new Error("Unknown error")
    });
  }, [onUploadError]);

  // Host callbacks change identity every render; keep them out of the web
  // pick effect deps so we don't re-open the OS dialog spuriously.
  const webPickCallbacksRef = useRef({
    onFilesLoaded,
    onOpenChange,
    onError,
    cleanupBeforeUpload
  });
  // Retires the live web attempt: cancels its no-response timer and closes it
  // with the given outcome. A replaced or abandoned dialog is not a hang, but
  // it has already emitted `UploadMethodSelected` — leaving it unterminated
  // would understate exactly the abandonment this funnel exists to measure.
  // Idempotent via the attempt's own `terminalEmitted` guard.
  const webRetireRef = useRef<((outcome: UploadPickerOutcome) => void) | null>(null);
  // Unmount (route change mid-dialog) ends the attempt the same way backing
  // out of the dialog does.
  useEffect(() => () => {
    webRetireRef.current?.("CANCELED");
  }, []);
  // Declared before the web pick effect so a generation bump lands on the ref
  // before that effect reads it in the same commit.
  useEffect(() => {
    currentPickerGenerationRef.current = pickerGeneration;
    webPickCallbacksRef.current = {
      onFilesLoaded,
      onOpenChange,
      onError,
      cleanupBeforeUpload
    };
  });
  const multiPhotoUpload = useCallback(async (images: {
    id: string;
    path: string;
  }[], record: AttemptRecord | undefined) => {
    if (images.length === 0) {
      return;
    }
    cleanupBeforeUpload?.();
    analyticsService.trackEvent("ClickedUpload", {
      type: "CAMERA"
    });
    let uploadFiles: UploadFile[];
    try {
      uploadFiles = await Promise.all(images.reverse().map(async (image, idx) => {
        const response = await fetch(image.path);
        const blob = await response.blob();
        if (!isSupportedImageType(blob.type)) {
          onUploadError({
            message: "This file type is not currently supported."
          });
          throw new UnsupportedFileTypeError(blob.type);
        }
        const filename = `${idx}-${generateFileName(blob.type)}`;
        return {
          name: filename,
          sizeBytes: blob.size,
          contentType: blob.type,
          blob,
          scanbotEntryPoint: entryPoint
        } as UploadFile;
      }));
    } catch (error) {
      // Scoped to blob building — `onFilesLoaded` below reaches the upload
      // provider, which emits its own terminal.
      trackUploadFailed(record, images.length, error instanceof UnsupportedFileTypeError ? "UNSUPPORTED_FILE_TYPE" : "BLOB_CONVERSION_FAILED");
      onError(error);
      onOpenChange(false);
      return;
    }
    try {
      // The consumer drives the close via its own `open` state. We
      // deliberately do NOT call `onOpenChange(false)` here — doing so
      // synchronously, mid-upload, races the dismiss animation and tears
      // the modal out (ENG-6644).
      await onFilesLoaded(uploadFiles, record?.attempt);
    } catch (error) {
      onError(error);
      onOpenChange(false);
    }
  }, [onError, onFilesLoaded, onUploadError, cleanupBeforeUpload, entryPoint, trackUploadFailed, onOpenChange]);
  const onImageSelected = async (images: ImagePickerAsset[]): Promise<void> => {
    const record = methodAttemptsRef.current.GALLERY;
    if (images.length === 0) {
      trackPickerReturned(record, "CANCELED", 0);
      onOpenChange(false);
      return;
    }
    trackPickerReturned(record, "SELECTED", images.length, {
      totalSizeBytes: sumDefinedSizes(images.map(image => image.fileSize))
    });
    analyticsService.trackEvent("ClickedUpload", {
      type: "GALLERY"
    });
    cleanupBeforeUpload?.();
    let uploadFiles: UploadFile[];
    try {
      uploadFiles = await Promise.all(images.map(async (image, idx) => {
        const blob = await assetToBlob(image);
        if (!isSupportedImageType(blob.type)) {
          onUploadError({
            message: "This file type is not currently supported."
          });
          throw new UnsupportedFileTypeError(blob.type);
        }
        const filename = `${idx}-${generateFileName(blob.type)}`;
        return {
          name: filename,
          sizeBytes: blob.size,
          contentType: blob.type,
          blob
        } as UploadFile;
      }));
    } catch (error) {
      trackUploadFailed(record, images.length, error instanceof UnsupportedFileTypeError ? "UNSUPPORTED_FILE_TYPE" : "BLOB_CONVERSION_FAILED");
      onError(error);
      onOpenChange(false);
      return;
    }
    try {
      // See multiPhotoUpload for why we don't close here (ENG-6644).
      await onFilesLoaded(uploadFiles, record?.attempt);
    } catch (error) {
      onError(error);
      onOpenChange(false);
    }
  };
  const onFilesSelected = async (files: DocumentPickerAsset[]): Promise<void> => {
    const record = methodAttemptsRef.current.FILE;
    if (files.length === 0) {
      trackPickerReturned(record, "CANCELED", 0);
      onOpenChange(false);
      return;
    }
    trackPickerReturned(record, "SELECTED", files.length, {
      totalSizeBytes: sumDefinedSizes(files.map(file => file.size))
    });
    analyticsService.trackEvent("ClickedUpload", {
      type: "FILE"
    });
    cleanupBeforeUpload?.();
    let uploadFiles: UploadFile[];
    try {
      uploadFiles = await documentAssetsToUploadFiles(files);
    } catch (error) {
      trackUploadFailed(record, files.length, "BLOB_CONVERSION_FAILED");
      onError(error);
      onOpenChange(false);
      return;
    }
    try {
      // See multiPhotoUpload for why we don't close here (ENG-6644).
      await onFilesLoaded(uploadFiles, record?.attempt);
    } catch (error) {
      onError(error);
      onOpenChange(false);
    }
  };
  const {
    pickImage,
    isLoading: imageLoading
  } = useImagePicker({
    onImageSelected,
    // These fire while the picker is still up, so the live record is the one
    // that opened it — unlike the async handlers, which capture at entry.
    onCanceled: () => trackPickerReturned(methodAttemptsRef.current.GALLERY, "CANCELED", 0),
    onError: () => trackPickerReturned(methodAttemptsRef.current.GALLERY, "ERROR", 0, {
      errorType: "PICKER_ERROR"
    }),
    onNoResponse: info => trackPickerReturned(methodAttemptsRef.current.GALLERY, "NO_RESPONSE", 0, noResponseExtra(info))
  });
  // Native only — web never renders these buttons. Backing out of the OS
  // picker returns to the chooser (tracked as a terminal, not a dismissal).
  const {
    pickFile,
    isLoading: fileLoading
  } = useFilePicker({
    onFilesSelected,
    onCanceled: () => trackPickerReturned(methodAttemptsRef.current.FILE, "CANCELED", 0),
    onError: () => trackPickerReturned(methodAttemptsRef.current.FILE, "ERROR", 0, {
      errorType: "PICKER_ERROR"
    }),
    onNoResponse: info => trackPickerReturned(methodAttemptsRef.current.FILE, "NO_RESPONSE", 0, noResponseExtra(info)),
    ...filePickerOptions
  });
  useEffect(() => {
    if (!isWeb || hasAlternativeAnswer) {
      return;
    }
    if (!open) {
      lastHandledGenerationRef.current = null;
      // The request was cleared with the dialog unanswered; a closed attempt
      // is abandonment, not a hang.
      webRetireRef.current?.("CANCELED");
      return;
    }
    if (lastHandledGenerationRef.current === pickerGeneration) {
      return;
    }
    lastHandledGenerationRef.current = pickerGeneration;
    const generation = pickerGeneration;
    const accept = filePickerOptions?.accept ?? [...DEFAULT_FILE_ACCEPT];
    const multiple = filePickerOptions?.multiple ?? true;

    // Web has no chooser: the trigger tap opens the OS dialog directly, so the
    // funnel's sheet-open and method steps collapse into one moment. Each
    // generation is one tap = one attempt, and the envelope is closed over
    // locally so a superseded pick's late resolution can't emit into the
    // newer attempt's session.
    const session = createUploadFunnelSession({
      entryPoint,
      issueId
    });
    sessionRef.current = session;
    methodSelectedRef.current = true;
    const attempt: UploadFunnelAttempt = {
      ...session,
      method: "FILE"
    };
    methodAttemptsRef.current.FILE = {
      attempt,
      openedAt: Date.now(),
      terminalEmitted: false
    };
    analyticsService.trackEvent("UploadInitiated", session);
    analyticsService.trackEvent("UploadMethodSelected", attempt);
    const openedAt = Date.now();
    let terminalEmitted = false;
    const returned = (outcome: UploadPickerOutcome, fileCount: number, extra?: {
      totalSizeBytes?: number;
      errorType?: UploadErrorType;
    }) => {
      if (terminalEmitted) {
        return;
      }
      terminalEmitted = true;
      analyticsService.trackEvent("UploadPickerReturned", {
        ...attempt,
        outcome,
        fileCount,
        ...(extra?.totalSizeBytes !== undefined ? {
          totalSizeBytes: extra.totalSizeBytes
        } : {}),
        ...(extra?.errorType ? {
          errorType: extra.errorType
        } : {}),
        durationMs: Date.now() - openedAt
      });
    };

    // A replaced dialog is not a hang, but it is a finished attempt: close the
    // previous generation as SUPERSEDED before this one arms its own timer.
    webRetireRef.current?.("SUPERSEDED");
    const cancelNoResponseTimer = startPickerNoResponseTimer(info => returned("NO_RESPONSE", 0, noResponseExtra(info)));
    webRetireRef.current = outcome => {
      cancelNoResponseTimer();
      returned(outcome, 0);
    };

    // Call getDocumentAsync directly (not useFilePicker) so this generation's
    // resolution can be ignored if a newer open superseded it mid-flight.
    void (async () => {
      const callbacks = () => webPickCallbacksRef.current;
      try {
        const result = await getDocumentAsync({
          type: accept,
          multiple
        });
        cancelNoResponseTimer();
        if (currentPickerGenerationRef.current !== generation) {
          return;
        }
        if (result.canceled || result.assets.length === 0) {
          returned("CANCELED", 0);
          callbacks().onOpenChange(false);
          return;
        }
        returned("SELECTED", result.assets.length, {
          totalSizeBytes: sumDefinedSizes(result.assets.map(a => a.size))
        });
        analyticsService.trackEvent("ClickedUpload", {
          type: "FILE"
        });
        let uploadFiles: UploadFile[];
        try {
          uploadFiles = await documentAssetsToUploadFiles(result.assets);
        } catch (error) {
          analyticsService.trackEvent("UploadFailed", {
            ...attempt,
            fileCount: result.assets.length,
            totalSizeBytes: sumDefinedSizes(result.assets.map(a => a.size)),
            errorType: "BLOB_CONVERSION_FAILED",
            durationMs: Date.now() - openedAt
          });
          throw error;
        }
        if (currentPickerGenerationRef.current !== generation) {
          return;
        }
        callbacks().cleanupBeforeUpload?.();
        // See multiPhotoUpload for why we don't close here (ENG-6644).
        await callbacks().onFilesLoaded(uploadFiles, attempt);
      } catch (error) {
        cancelNoResponseTimer();
        if (currentPickerGenerationRef.current !== generation) {
          return;
        }
        returned("ERROR", 0, {
          errorType: "PICKER_ERROR"
        });
        callbacks().onError(error);
        callbacks().onOpenChange(false);
      }
    })();
  }, [open, pickerGeneration, filePickerOptions?.accept, filePickerOptions?.multiple, entryPoint, issueId, hasAlternativeAnswer]);

  // Web: one file-picker path (PDF + images). No chooser sheet — open the OS
  // dialog directly. Composition (images XOR one PDF) is enforced when the
  // batch is uploaded as a single document. With an alternative answer the
  // chooser renders on web too (see hasAlternativeAnswer above), with the
  // native-only camera and library options omitted.
  if (isWeb && !hasAlternativeAnswer) {
    return null;
  }
  return <Modal open={open} onOpenChange={handleOpenChange} size="small" headerProps={subtitle ? {
    title,
    variant: "largeTitle",
    subtitle
  } : {
    title
  }} data-sentry-element="Modal" data-sentry-component="UploadDocumentSheet" data-sentry-source-file="UploadDocumentSheet.tsx">
      <YStack gap="$sp1" paddingVertical={0} data-sentry-element="YStack" data-sentry-source-file="UploadDocumentSheet.tsx">
        {isWeb ? null : <CameraPickerButton isLoading={useCamera} onPermissionCheckStart={onCameraPermissionCheckStart} onPermissionCheckComplete={onCameraPermissionCheckComplete} onPermissionDenied={onCameraPermissionDenied} setIsLoading={setUseCamera} openCamera={openCamera} onPressed={() => selectMethod("SCANNER")} onPermissionBlocked={() => trackPickerReturned(methodAttemptsRef.current.SCANNER, "ERROR", 0, {
        errorType: "PICKER_PERMISSION_DENIED"
      })} />}
        {isWeb ? null : <ResourceCard kind="button" icon={ImageIcon} label="Choose from library" loading={imageLoading} disabled={imageLoading} size="large" onPress={() => {
        selectMethod("GALLERY");
        void pickImage();
      }} aria-label="Choose from library" />}
        <ResourceCard kind="button" icon={FileUpIcon} label="Upload a file" loading={fileLoading} disabled={fileLoading} size="large" onPress={() => {
        selectMethod("FILE");
        void pickFile();
      }} aria-label="Upload a file" testID="case-preview.choose-file" data-sentry-element="ResourceCard" data-sentry-source-file="UploadDocumentSheet.tsx" />
        {alternativeAnswer ? <ResourceCard kind="button" icon={alternativeAnswer.icon} label={alternativeAnswer.label} size="large" onPress={alternativeAnswer.onPress} aria-label={alternativeAnswer.label} {...alternativeAnswer.testID ? {
        testID: alternativeAnswer.testID
      } : {}} /> : null}
      </YStack>
    </Modal>;
};